/* eslint-disable react/prop-types */
import React, {
  createContext, useEffect, useReducer, useState
} from 'react';
import jwtDecode from 'jwt-decode';
import SplashScreen from 'src/components/SplashScreen';
import axios from 'src/utils/AxiosInstance';
import Commons from 'src/utils/Commons';
import Api from 'src/utils/Api';

const initialAuthState = {
  isAuthenticated: false,
  isManager: 0,
  isInitialised: false,
  user: null
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;
      let isManager = 0;
      if (user) isManager = user.manager_flag;
      return {
        ...state,
        isAuthenticated,
        isManager,
        isInitialised: true,

        user
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        isManager: false,
        user: null
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve(),
  currentUser: null
});

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const login = async (email, password) => {
    const response = await Api.post(Api.getApiUrl('auth/login_admin'), { email, password });

    // console.log('accessToken', response);
    const { accessToken } = response.data;
    const { user } = response.data;
    setCurrentUser(user);
    Commons.setSession(accessToken);
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const logout = () => {
    Commons.setSession(null);
    setCurrentUser(null);
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (email, name, password) => {
    const response = await axios.post(Api.getApiUrl('register'), {
      email,
      name,
      password
    });
    const accessToken = response.data.access_token;
    const { user } = response.data;
    setCurrentUser(user);
    window.localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  const getUserRole = () => {
    if (!currentUser) {
      return 'guest';
    }
    if (currentUser.roles === 'admin') {
      return 'admin';
    }

    return 'staff';
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          Commons.setSession(accessToken);

          const response = await Api.post(Api.getApiUrl('auth/me_admin'), null, { hostType: 'auth' });
          const user = response.data;
          setCurrentUser(user);

          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: true,
              isManager: user.manager_flag,
              user
            }
          });
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: false,
              isManager: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register,
        getUserRole,
        currentUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
